import { SettingKey } from "@serophin/nottldr-shared";
import { Mail } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { SettingsCard } from "./SettingsCard";
import { Toggle } from "./Toggle";
import type { SettingsState } from "./useSettingsState";

const SELECT =
  "px-2 py-1 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none";

const Row = ({ title, description, control }: { title: string; description: string; control: ComponentChildren }) => (
  <div class="pb-4 border-b border-gray-200 dark:border-gray-700">
    <div class="flex items-center justify-between mb-1">
      <h3 class="text-sm font-medium text-gray-900 dark:text-white">{title}</h3>
      {control}
    </div>
    <p class="text-xs text-gray-600 dark:text-gray-400">{description}</p>
  </div>
);

const Frequency = ({
  settings,
  settingKey,
}: {
  settings: SettingsState;
  settingKey: typeof SettingKey.EMAIL_FEED_UPDATES | typeof SettingKey.EMAIL_RECOMMENDATIONS;
}) => (
  <select value={settings.get(settingKey)} onChange={(event) => settings.change([{ key: settingKey, value: event.currentTarget.value }])} class={SELECT}>
    <option value="weekly">Weekly</option>
    <option value="monthly">Monthly</option>
    <option value="never">Never</option>
  </select>
);

const OnOff = ({
  settings,
  settingKey,
}: {
  settings: SettingsState;
  settingKey: typeof SettingKey.EMAIL_PRODUCT_ANNOUNCEMENTS | typeof SettingKey.EMAIL_MARKETING;
}) => <Toggle checked={settings.get(settingKey) === "on"} onChange={(on) => settings.change([{ key: settingKey, value: on ? "on" : "off" }])} />;

const turnOffAll = (settings: SettingsState): void =>
  settings.change([
    { key: SettingKey.EMAIL_FEED_UPDATES, value: "never" },
    { key: SettingKey.EMAIL_RECOMMENDATIONS, value: "never" },
    { key: SettingKey.EMAIL_PRODUCT_ANNOUNCEMENTS, value: "off" },
    { key: SettingKey.EMAIL_MARKETING, value: "off" },
  ]);

export const EmailPreferences = ({ settings }: { settings: SettingsState }) => (
  <SettingsCard icon={Mail} title="Email Preferences">
    <div class="space-y-5">
      <Row
        title="Transactional Emails"
        description="Essential emails about your account and security. These cannot be disabled."
        control={<Toggle checked={true} onChange={() => {}} disabled={true} />}
      />
      <Row
        title="Feed Updates"
        description="Get curated summaries of the latest articles from your favorite topics and authors."
        control={<Frequency settings={settings} settingKey={SettingKey.EMAIL_FEED_UPDATES} />}
      />
      <Row
        title="Recommendations"
        description="Personalized article recommendations based on your reading history and preferences."
        control={<Frequency settings={settings} settingKey={SettingKey.EMAIL_RECOMMENDATIONS} />}
      />
      <Row
        title="Product Announcements"
        description="Stay informed about new features, improvements, and important platform updates."
        control={<OnOff settings={settings} settingKey={SettingKey.EMAIL_PRODUCT_ANNOUNCEMENTS} />}
      />
      <Row
        title="Marketing"
        description="Receive promotional content, special offers, and newsletters about our services."
        control={<OnOff settings={settings} settingKey={SettingKey.EMAIL_MARKETING} />}
      />
      <button
        type="button"
        onClick={() => turnOffAll(settings)}
        class="px-2 py-1 text-xs font-medium text-gray-800 dark:text-gray-300 bg-gray-100 dark:bg-neutral-700 hover:bg-gray-200 dark:hover:bg-neutral-600 rounded-lg transition-colors"
      >
        Turn off optional emails
      </button>
    </div>
  </SettingsCard>
);
